import React from 'react';
import { motion } from 'framer-motion';
import { AlertTriangle, Info, Shield, FileText, Mail } from 'lucide-react';

const Disclaimer = () => {
  const disclaimerSections = [
    {
      title: "Service Availability",
      icon: Info,
      content: `While Aquargin strives to maintain 99.9% uptime, we cannot guarantee uninterrupted service. Our platform may experience downtime for maintenance, updates, or due to circumstances beyond our control. We will provide advance notice when possible and work to minimize service disruptions.`
    },
    {
      title: "Information Accuracy",
      icon: FileText,
      content: `The information provided on our website and platform is for general informational purposes. While we strive to keep information accurate and up-to-date, we make no representations or warranties about the completeness, accuracy, or reliability of any information on our platform.`
    },
    {
      title: "Third-Party Services",
      icon: Shield,
      content: `Our platform may integrate with third-party services and applications. We are not responsible for the availability, accuracy, or content of these third-party services. Your use of third-party services is subject to their respective terms and conditions.`
    },
    {
      title: "Results and Performance",
      icon: AlertTriangle,
      content: `Case studies, testimonials, and performance metrics shared on our platform represent specific client experiences and results. Individual results may vary based on numerous factors including business type, implementation approach, and user engagement. Past performance does not guarantee future results.`
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary/10 via-white to-secondary/5 py-20 lg:py-32">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-secondary/5 opacity-40"></div>
        </div>
        
        <div className="container mx-auto px-6 relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="flex items-center justify-center mb-6"
            >
              <div className="bg-gradient-to-r from-orange-100 to-red-100 rounded-full p-4 mr-4">
                <AlertTriangle className="h-8 w-8 text-orange-600" />
              </div>
              <h1 className="text-4xl lg:text-6xl font-bold text-gray-900">
                Disclaimer
              </h1>
            </motion.div>
            
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-xl text-gray-600 mb-8"
            >
              Important information about the use of our services and platform.
            </motion.p>
          </div>
        </div>
      </section>

      {/* General Disclaimer */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto"
          >
            <div className="bg-gradient-to-r from-orange-50 to-red-50 rounded-xl p-8 mb-12 border-l-4 border-orange-500">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">General Disclaimer</h2>
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                The information contained on this website and platform is provided on an "as is" basis. 
                To the fullest extent permitted by law, Aquargin excludes all representations, warranties, 
                and conditions relating to our website and the use of this platform.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                Nothing on this website constitutes, or is meant to constitute, advice of any kind. 
                If you require advice in relation to any legal, financial, or medical matter, 
                you should consult an appropriate professional.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Specific Disclaimers */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6"
            >
              Specific Disclaimers
            </motion.h2>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="grid gap-8">
              {disclaimerSections.map((section, index) => {
                const Icon = section.icon;
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-white rounded-xl p-8 shadow-lg"
                  >
                    <div className="flex items-start space-x-4">
                      <div className="bg-gradient-to-r from-orange-100 to-red-100 rounded-lg p-3 flex-shrink-0">
                        <Icon className="h-6 w-6 text-orange-600" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-gray-900 mb-4">
                          {section.title}
                        </h3>
                        <p className="text-gray-700 leading-relaxed">
                          {section.content}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Limitation of Liability */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto"
          >
            <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Limitation of Liability</h2>
              
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  <strong>In no event shall Aquargin be liable for any direct, indirect, incidental, 
                  special, consequential, or punitive damages, including but not limited to:</strong>
                </p>
                
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Loss of profits, data, or use</li>
                  <li>Business interruption or loss of business opportunities</li>
                  <li>Damage to reputation or goodwill</li>
                  <li>Costs of replacement goods or services</li>
                  <li>Any other commercial damages or losses</li>
                </ul>
                
                <p>
                  This limitation applies regardless of the legal theory on which the claim is based, 
                  whether in contract, tort (including negligence), strict liability, or otherwise, 
                  even if Aquargin has been advised of the possibility of such damages.
                </p>
                
                <p>
                  Some jurisdictions do not allow the exclusion or limitation of liability for 
                  consequential or incidental damages, so the above limitation may not apply to you.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 bg-gradient-to-r from-orange-600 to-red-600">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto text-center"
          >
            <Mail className="h-12 w-12 text-white mx-auto mb-6" />
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
              Questions About This Disclaimer?
            </h2>
            <p className="text-xl text-white/90 mb-8">
              If you have any questions about this disclaimer or need clarification on any terms, 
              please don't hesitate to contact our legal team.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <a
                href="mailto:legal@aquargin.com"
                className="bg-white text-orange-600 px-8 py-4 rounded-lg font-bold text-lg hover:shadow-xl transition-all duration-300"
              >
                Contact Legal Team
              </a>
              <a
                href="/contact"
                className="border-2 border-white text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-white hover:text-orange-600 transition-all duration-300"
              >
                General Contact
              </a>
            </div>

            <div className="text-white/80 text-sm">
              <p>Last updated: January 1, 2025</p>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Disclaimer; 